import React, { useEffect, useId, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

// A dialog that behaves like one: role and label for assistive tech, focus moved inside on open
// and kept there with Tab, Escape and the backdrop close it, the page behind doesn't scroll, and
// the width fits a phone.
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Modal: React.FC<{ title: string; description?: string; onClose: () => void; children: React.ReactNode; size?: 'sm' | 'md' | 'lg' }> = ({ title, description, onClose, children, size = 'md' }) => {
  const titleId = useId();
  const descId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    opener.current = document.activeElement as HTMLElement | null;
    const first = panel.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first || panel.current)?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab' || !panel.current) return;
      const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const firstEl = items[0], lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      opener.current?.focus?.();
    };
  }, [onClose]);

  const width = size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-2xl' : 'max-w-md';
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-gray-600/50 dark:bg-gray-900/70" onClick={onClose} aria-hidden="true" />
      <div className="flex min-h-full items-start justify-center p-4 sm:pt-20">
        <div
          ref={panel}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descId : undefined}
          tabIndex={-1}
          className={`relative w-full ${width} rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-lg dark:shadow-gray-900/50 outline-none`}
        >
          <div className="flex items-start justify-between gap-3 mb-2">
            <h3 id={titleId} className="text-lg font-bold text-gray-900 dark:text-gray-100">{title}</h3>
            <button type="button" onClick={onClose} aria-label="Close" className="shrink-0 rounded p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
          {description && <p id={descId} className="text-sm text-gray-600 dark:text-gray-400 mb-4">{description}</p>}
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
